/**
 * DeviceHandoffService
 *
 * Manages seamless device-to-device call transfers via Bluetooth proximity.
 *
 * Responsibilities:
 * - BLE scanning for companion devices (Web Bluetooth API / react-native-ble-plx)
 * - QR code fallback for browsers without Web Bluetooth
 * - Coordinating with the server handoff API (prepare/execute)
 * - Tracking handoff state and emitting events for Redux
 *
 * Flow:
 * 1. startScanning() → discovers nearby BLE devices running MalkaVRS
 * 2. User confirms transfer → initiateHandoff()
 * 3. Server creates one-time token → token sent to target via BLE/QR
 * 4. Target device receives token → receiveHandoff()
 * 5. Target joins room → confirmTrackEstablished()
 * 6. Original device leaves room gracefully
 */
import { queueTelemetryEvent } from '../mobile/navigation/telemetryQueue';
import { apiClient, unwrapApiResponse } from '../shared/api-client';
import { getPersistentJson, getPersistentItem, setPersistentItem } from '../vrs-auth/storage';
// ============================================
// BLE CONSTANTS
// ============================================
// Custom GATT service UUID for MalkaVRS handoff
const MALKA_VRS_SERVICE_UUID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';
// Characteristic: read → returns hashed account ID for same-account verification
const ACCOUNT_CHAR_UUID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567891';
// Characteristic: write → receives handoff token
const HANDOFF_CHAR_UUID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567892';
const HANDOFF_DEVICE_ID_STORAGE_KEY = 'malka_device_id';
const RSSI_AT_ONE_METER = -59;
const PATH_LOSS_EXPONENT = 2.2;
const PROXIMITY_SAMPLE_INTERVAL_MS = 3000;
const CONTACT_DISTANCE_METERS = 0.12;
const NEAR_DISTANCE_METERS = 1.2;
const SOURCE_OFFER_TIMEOUT_MS = 65 * 1000;
const SOURCE_COMPLETION_TIMEOUT_MS = 95 * 1000;
const TARGET_MEDIA_TIMEOUT_MS = 30 * 1000;
const HANDOFF_HTTP_TIMEOUT_MS = 7000;
const COMPLETION_RETRY_DELAYS_MS = [0, 1000, 2500, 5000, 10000, 15000];
function getDefaultServerUrl() {
    if (typeof window !== 'undefined') {
        return window.location.origin;
    }
    return 'http://localhost:3001';
}
function getHandoffConfig() {
    const defaults = {
        serverUrl: getDefaultServerUrl(),
        bleScanInterval: 5000,
        autoProximityHandoffDistanceMeters: CONTACT_DISTANCE_METERS,
        autoProximityHandoffEnabled: false
    };
    if (typeof config !== 'undefined' && config.vrs) {
        return { ...defaults, ...config.vrs };
    }
    return defaults;
}
// ============================================
// PLATFORM DETECTION
// ============================================
function isWebBluetoothSupported() {
    return typeof navigator !== 'undefined' && typeof navigator.bluetooth === 'object';
}
function isReactNative() {
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
}
function getStoredAccountId() {
    try {
        return getPersistentJson('vrs_user_info')?.id || null;
    }
    catch {
        return null;
    }
}
function getStoredUserId() {
    try {
        return getPersistentJson('vrs_user_info')?.id || null;
    }
    catch {
        return null;
    }
}
function getErrorMessage(error, fallback) {
    return error instanceof Error ? error.message : fallback;
}
function hasErrorName(error, name) {
    return typeof error === 'object'
        && error !== null
        && 'name' in error
        && error.name === name;
}
function getStorageFlag(key) {
    try {
        return getPersistentItem(key) === 'true';
    }
    catch {
        return false;
    }
}
function estimateDistanceMeters(rssi) {
    if (!Number.isFinite(rssi) || rssi >= 0) {
        return undefined;
    }
    const distance = Math.pow(10, (RSSI_AT_ONE_METER - rssi) / (10 * PATH_LOSS_EXPONENT));
    return Math.max(0.02, Math.min(30, Number(distance.toFixed(2))));
}
function getProximityBucket(distanceMeters) {
    if (typeof distanceMeters !== 'number') {
        return 'unknown';
    }
    if (distanceMeters <= CONTACT_DISTANCE_METERS) {
        return 'contact';
    }
    if (distanceMeters <= NEAR_DISTANCE_METERS) {
        return 'near';
    }
    return 'room';
}
function getProximityConfidence(rssi, distanceMeters) {
    if (typeof distanceMeters !== 'number') {
        return 'low';
    }
    if (rssi >= -45) {
        return 'high';
    }
    if (rssi >= -65) {
        return 'medium';
    }
    return 'low';
}
// ============================================
// SERVICE
// ============================================
class DeviceHandoffService {
    constructor() {
        this.listeners = new Map();
        this.scanning = false;
        this.handoffInProgress = false;
        this.currentHandoffToken = null;
        this.companionDevices = new Map();
        this.scanInterval = null;
        this.bleDevice = null;
        this.userId = null;
        this.autoHandoffStartedForDevice = new Set();
        this.lastProximityTelemetryAt = new Map();
        this.receivedHandoffToken = null;
        this.acceptedHandoff = null;
        this.sourceHandoffTimeout = null;
        this.targetMediaTimeout = null;
        this.completionInFlight = false;
        this.completionAttempt = 0;
        // React Native BLE Manager (lazy-loaded)
        this.rnBleManager = null;
        this.config = getHandoffConfig();
        this.userId = getStoredUserId();
        this.deviceId = this.generateDeviceId();
        if (isReactNative()) {
            this.initReactNativeBLE();
        }
    }
    generateDeviceId() {
        let id = getPersistentItem(HANDOFF_DEVICE_ID_STORAGE_KEY);
        if (!id) {
            id = `device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            setPersistentItem(HANDOFF_DEVICE_ID_STORAGE_KEY, id);
        }
        return id;
    }
    async initReactNativeBLE() {
        try {
            const { BleManager } = require('react-native-ble-plx');
            this.rnBleManager = new BleManager();
        }
        catch {
            console.warn('[DeviceHandoff] react-native-ble-plx not available');
        }
    }
    // ============================================
    // EVENT SYSTEM
    // ============================================
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    off(event, callback) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            const index = eventListeners.indexOf(callback);
            if (index > -1) {
                eventListeners.splice(index, 1);
            }
        }
    }
    emit(event, data) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            eventListeners.forEach(cb => {
                try {
                    cb(data);
                }
                catch (error) {
                    console.error(`[DeviceHandoff] Error in listener for ${event}:`, error);
                }
            });
        }
    }
    clearSourceHandoffTimeout() {
        if (this.sourceHandoffTimeout) {
            clearTimeout(this.sourceHandoffTimeout);
            this.sourceHandoffTimeout = null;
        }
    }
    clearTargetMediaTimeout() {
        if (this.targetMediaTimeout) {
            clearTimeout(this.targetMediaTimeout);
            this.targetMediaTimeout = null;
        }
    }
    startSourceHandoffTimeout(timeoutMs = SOURCE_OFFER_TIMEOUT_MS) {
        this.clearSourceHandoffTimeout();
        this.sourceHandoffTimeout = setTimeout(() => {
            this.handoffInProgress = false;
            const token = this.currentHandoffToken;
            this.currentHandoffToken = null;
            const progress = {
                stage: 'failed',
                message: 'Move timed out. Your call is still active on this device.',
                error: 'The other device did not finish joining.'
            };
            this.emit('handoff_source_timeout', { token });
            this.emit('handoff_failed', progress);
            this.emit('handoff_progress', progress);
        }, timeoutMs);
    }
    startTargetMediaTimeout() {
        this.clearTargetMediaTimeout();
        this.targetMediaTimeout = setTimeout(() => {
            if (!this.acceptedHandoff) {
                return;
            }
            const progress = {
                stage: 'failed',
                message: 'This device could not establish video. The original device remains in the call.',
                error: 'Target device media did not become ready.'
            };
            this.abortAcceptedHandoff(progress);
        }, TARGET_MEDIA_TIMEOUT_MS);
    }
    abortAcceptedHandoff(progress) {
        const accepted = this.acceptedHandoff;
        if (!accepted) {
            return;
        }
        this.clearTargetMediaTimeout();
        this.acceptedHandoff = null;
        this.receivedHandoffToken = null;
        this.completionInFlight = false;
        this.completionAttempt = 0;
        // Fire the authenticated rollback before leaving locally. If the
        // network is unavailable, the source-side timeout restores its own
        // session registration without ending the call.
        void this.callServer('POST', '/api/handoff/abort', {
            completionToken: accepted.completionToken,
            newDeviceId: this.deviceId,
            roomName: accepted.roomName
        }).catch(error => {
            console.warn('[DeviceHandoff] Could not notify server of target abort:', error);
        });
        this.emit('handoff_target_abort', progress);
        this.emit('handoff_failed', progress);
        this.emit('handoff_progress', progress);
    }
    // ============================================
    // SCANNING (Web Bluetooth)
    // ============================================
    /**
     * Start scanning for companion devices via BLE.
     * On web, uses the Web Bluetooth API (Chrome/Edge).
     * On mobile, uses react-native-ble-plx.
     */
    async startScanning() {
        if (this.scanning) {
            return;
        }
        this.userId = getStoredUserId();
        if (!this.userId) {
            console.warn('[DeviceHandoff] Cannot scan without a userId');
            return;
        }
        this.scanning = true;
        this.emit('handoff_progress', {
            stage: 'scanning',
            message: 'Scanning for nearby devices...'
        });
        if (isReactNative()) {
            this.startNativeScanning();
        }
        else if (isWebBluetoothSupported()) {
            // Web Bluetooth doesn't support background scanning —
            // we rely on periodic checks and device discovery via requestDevice.
            // Start periodic scan attempts.
            this.scanInterval = setInterval(() => {
                this.checkForCompanions();
            }, this.config.bleScanInterval);
        }
        else {
            console.info('[DeviceHandoff] BLE not supported — use QR code fallback');
            this.scanning = false;
        }
    }
    /**
     * Stop scanning for companion devices.
     */
    stopScanning() {
        this.scanning = false;
        if (this.scanInterval) {
            clearInterval(this.scanInterval);
            this.scanInterval = null;
        }
        if (this.rnBleManager) {
            this.rnBleManager.stopDeviceScan();
        }
    }
    /**
     * Web Bluetooth: attempt to discover a companion device.
     * Note: Web Bluetooth requires user gesture for requestDevice(),
     * so this is a best-effort check using cached/connected devices.
     */
    async checkForCompanions() {
        if (!this.scanning || !navigator.bluetooth) {
            return;
        }
        try {
            // Use getDevices() if available (Chrome 96+) to get previously paired devices
            if (typeof navigator.bluetooth.getDevices === 'function') {
                const devices = await navigator.bluetooth.getDevices();
                for (const device of devices) {
                    if (device.name && device.name.startsWith('MalkaVRS')) {
                        const companion = {
                            id: device.id,
                            name: device.name,
                            rssi: -50 // RSSI not available via getDevices, estimate
                        };
                        this.addCompanionDevice(companion);
                    }
                }
            }
        }
        catch {
            // Silently ignore — BLE scanning is opportunistic
        }
    }
    /**
     * Prompt user to select a BLE device (requires user gesture on web).
     * Call this from a button click handler.
     */
    async requestCompanionDevice() {
        if (!isWebBluetoothSupported()) {
            console.warn('[DeviceHandoff] Web Bluetooth not supported');
            return null;
        }
        try {
            const device = await navigator.bluetooth.requestDevice({
                filters: [{ services: [MALKA_VRS_SERVICE_UUID] }],
                optionalServices: [ACCOUNT_CHAR_UUID, HANDOFF_CHAR_UUID]
            });
            this.bleDevice = device;
            const companion = {
                id: device.id,
                name: device.name || 'Unknown Device',
                rssi: -40 // nearby since user selected it
            };
            // Verify same account
            const sameAccount = await this.verifySameAccount(device);
            if (!sameAccount) {
                console.warn('[DeviceHandoff] Device is logged into a different account');
                return null;
            }
            this.addCompanionDevice(companion);
            return companion;
        }
        catch (error) {
            if (hasErrorName(error, 'NotFoundError')) {
                // User cancelled the picker
                return null;
            }
            console.error('[DeviceHandoff] Error requesting device:', error);
            return null;
        }
    }
    async verifySameAccount(device) {
        try {
            const server = await device.gatt?.connect();
            if (!server) {
                return false;
            }
            const service = await server.getPrimaryService(MALKA_VRS_SERVICE_UUID);
            const char = await service.getCharacteristic(ACCOUNT_CHAR_UUID);
            const value = await char.readValue();
            const decoder = new TextDecoder();
            const remoteAccountId = decoder.decode(value);
            const localAccountId = getStoredAccountId();
            // Disconnect after reading
            device.gatt?.disconnect();
            return localAccountId !== null && remoteAccountId === localAccountId;
        }
        catch (error) {
            console.warn('[DeviceHandoff] Could not verify account:', error);
            return false;
        }
    }
    // ============================================
    // SCANNING (React Native)
    // ============================================
    startNativeScanning() {
        if (!this.rnBleManager) {
            return;
        }
        this.rnBleManager.startDeviceScan([MALKA_VRS_SERVICE_UUID], null, (error, device) => {
            if (error) {
                console.error('[DeviceHandoff] BLE scan error:', error);
                return;
            }
            if (device?.id && device.name?.startsWith('MalkaVRS')) {
                const companion = {
                    id: device.id,
                    name: device.name,
                    rssi: device.rssi || -100
                };
                this.addCompanionDevice(companion);
            }
        });
    }
    addCompanionDevice(device) {
        const estimatedDistanceMeters = estimateDistanceMeters(device.rssi);
        const enrichedDevice = {
            ...device,
            estimatedDistanceMeters,
            lastSeenAt: Date.now(),
            proximityBucket: getProximityBucket(estimatedDistanceMeters),
            proximityConfidence: getProximityConfidence(device.rssi, estimatedDistanceMeters)
        };
        const isNew = !this.companionDevices.has(enrichedDevice.id);
        this.companionDevices.set(enrichedDevice.id, enrichedDevice);
        if (isNew) {
            console.log(`[DeviceHandoff] Companion found: ${enrichedDevice.name} (${enrichedDevice.id})`);
            this.emit('device_found', enrichedDevice);
        }
        this.emit('proximity_sample', enrichedDevice);
        this.maybeEmitProximityTelemetry(enrichedDevice);
        this.maybeAutoInitiateProximityHandoff(enrichedDevice);
    }
    maybeEmitProximityTelemetry(device) {
        const now = Date.now();
        const last = this.lastProximityTelemetryAt.get(device.id) || 0;
        if (now - last < PROXIMITY_SAMPLE_INTERVAL_MS) {
            return;
        }
        this.lastProximityTelemetryAt.set(device.id, now);
        queueTelemetryEvent({
            event: 'handoff_proximity_sample',
            metadata: {
                deviceId: device.id,
                deviceName: device.name,
                estimatedDistanceMeters: device.estimatedDistanceMeters,
                proximityBucket: device.proximityBucket,
                proximityConfidence: device.proximityConfidence,
                rssi: device.rssi
            },
            platform: isReactNative() ? 'native' : 'web',
            state: device.proximityBucket || 'unknown',
            surface: isReactNative() ? 'mobile' : 'web'
        }, { routine: true });
    }
    shouldAutoInitiateProximityHandoff(device) {
        const threshold = this.config.autoProximityHandoffDistanceMeters || CONTACT_DISTANCE_METERS;
        return Boolean(this.config.autoProximityHandoffEnabled || getStorageFlag('vrs_auto_handoff_on_contact'))
            && !this.handoffInProgress
            && !this.autoHandoffStartedForDevice.has(device.id)
            && typeof device.estimatedDistanceMeters === 'number'
            && device.estimatedDistanceMeters <= threshold
            && device.proximityConfidence !== 'low';
    }
    maybeAutoInitiateProximityHandoff(device) {
        if (!this.shouldAutoInitiateProximityHandoff(device)) {
            return;
        }
        this.autoHandoffStartedForDevice.add(device.id);
        void this.initiateHandoff(device, true);
    }
    // ============================================
    // HANDOFF INITIATION (sending device)
    // ============================================
    /**
     * Initiate a handoff to the specified companion device.
     * Called by the user on the sending device.
     */
    async initiateHandoff(companionDevice, initiatedByAutoProximity = false) {
        if (this.handoffInProgress) {
            return {
                stage: 'failed',
                message: 'Handoff already in progress'
            };
        }
        this.userId = getStoredUserId();
        if (!this.userId) {
            return {
                stage: 'failed',
                message: 'No user ID — cannot initiate handoff'
            };
        }
        this.handoffInProgress = true;
        this.emit('handoff_started', { companionDevice });
        this.emit('handoff_progress', {
            stage: 'preparing',
            message: 'Preparing handoff...',
            companionDevice
        });
        try {
            // Step 1: Ask the server to create a one-time handoff token
            const prepareResult = await this.callServer('POST', '/api/handoff/prepare', {
                autoProximity: initiatedByAutoProximity,
                bindTargetDevice: companionDevice.source === 'server-presence',
                userId: this.userId,
                targetDeviceId: companionDevice.id
            });
            this.currentHandoffToken = prepareResult.token;
            this.startSourceHandoffTimeout();
            this.emit('handoff_progress', {
                stage: 'transferring',
                message: 'Sending to target device...',
                companionDevice
            });
            // Server-presence devices already received the authenticated offer
            // from /prepare. Trying BLE here only creates a false fallback.
            if (companionDevice.source === 'server-presence') {
                const progress = {
                    stage: 'transferring',
                    message: 'Waiting for the other device to accept...',
                    companionDevice
                };
                this.emit('handoff_progress', progress);
                return progress;
            }
            // Step 2: Send the token to the companion device
            const sent = await this.sendTokenToCompanion(companionDevice, prepareResult.token);
            if (!sent) {
                // Fallback: return QR code data
                console.log('[DeviceHandoff] BLE send failed — use QR fallback');
                const progress = {
                    stage: 'transferring',
                    message: 'Open this move link on the target device.',
                    companionDevice,
                    error: 'BLE move not available — use move link',
                    qrUrl: this.getHandoffQRData() || undefined
                };
                this.emit('handoff_progress', progress);
                return progress;
            }
            return {
                stage: 'transferring',
                message: 'Token sent to target device',
                companionDevice
            };
        }
        catch (error) {
            this.clearSourceHandoffTimeout();
            this.handoffInProgress = false;
            const message = getErrorMessage(error, 'Handoff failed');
            const progress = {
                stage: 'failed',
                message,
                error: message
            };
            this.emit('handoff_failed', progress);
            this.emit('handoff_progress', progress);
            return progress;
        }
    }
    /**
     * Send the handoff token to a companion device via BLE.
     */
    async sendTokenToCompanion(companion, token) {
        if (isReactNative() && this.rnBleManager) {
            return this.sendTokenNative(companion.id, token);
        }
        if (isWebBluetoothSupported() && this.bleDevice) {
            return this.sendTokenWebBluetooth(token);
        }
        return false;
    }
    async sendTokenWebBluetooth(token) {
        try {
            const server = await this.bleDevice?.gatt?.connect();
            if (!server) {
                return false;
            }
            const service = await server.getPrimaryService(MALKA_VRS_SERVICE_UUID);
            const char = await service.getCharacteristic(HANDOFF_CHAR_UUID);
            const encoder = new TextEncoder();
            await char.writeValue(encoder.encode(token));
            this.bleDevice?.gatt?.disconnect();
            return true;
        }
        catch (error) {
            console.error('[DeviceHandoff] BLE write failed:', error);
            return false;
        }
    }
    async sendTokenNative(deviceId, token) {
        if (!this.rnBleManager) {
            return false;
        }
        try {
            const device = await this.rnBleManager.connectToDevice(deviceId);
            await device.discoverAllServicesAndCharacteristics();
            await device.writeCharacteristicWithResponseForService(MALKA_VRS_SERVICE_UUID, HANDOFF_CHAR_UUID, btoa(token));
            await this.rnBleManager.cancelDeviceConnection(deviceId);
            return true;
        }
        catch (error) {
            console.error('[DeviceHandoff] Native BLE write failed:', error);
            return false;
        }
    }
    // ============================================
    // HANDOFF RECEPTION (receiving device)
    // ============================================
    /**
     * Called when this device receives a handoff token
     * (either via BLE characteristic write, QR scan, or WebSocket).
     */
    async receiveHandoff(token) {
        this.receivedHandoffToken = token;
        this.emit('handoff_received', { token });
        return {
            stage: 'found',
            message: 'Handoff token received'
        };
    }
    /**
     * Accept a handoff by redeeming the token on the server and getting room info.
     */
    async acceptHandoff(token) {
        try {
            this.emit('handoff_progress', {
                stage: 'establishing',
                message: 'Joining call on this device...'
            });
            const result = await this.callServer('POST', '/api/handoff/execute', {
                token,
                newDeviceId: this.deviceId
            });
            this.acceptedHandoff = {
                completionToken: result.completionToken,
                completionExpiresAt: result.completionExpiresAt || Date.now() + 85 * 1000,
                conferenceJoined: false,
                handoffToken: token,
                roomName: result.roomName
            };
            this.completionAttempt = 0;
            this.receivedHandoffToken = null;
            this.startTargetMediaTimeout();
            this.emit('handoff_accepted', result);
            this.emit('handoff_progress', {
                stage: 'establishing',
                message: `Joining room ${result.roomName}...`
            });
            return result;
        }
        catch (error) {
            const message = getErrorMessage(error, 'Handoff acceptance failed');
            const progress = {
                stage: 'failed',
                message,
                error: message
            };
            this.emit('handoff_failed', progress);
            this.emit('handoff_progress', progress);
            return progress;
        }
    }
    /**
     * Decline a handoff offer.
     */
    async declineHandoff(token) {
        try {
            await this.callServer('POST', '/api/handoff/cancel', { token });
            this.receivedHandoffToken = null;
            this.emit('handoff_declined', { token });
        }
        catch (error) {
            const message = getErrorMessage(error, 'Unable to decline move');
            this.emit('handoff_failed', { error: message, message, stage: 'failed' });
        }
    }
    // ============================================
    // HANDOFF COMPLETION
    // ============================================
    /**
     * Confirm that the receiving device's video track is established.
     * Signals to the sending device that it's safe to leave.
     */
    noteConferenceJoined(roomName) {
        if (this.acceptedHandoff?.roomName === roomName) {
            this.acceptedHandoff.conferenceJoined = true;
        }
    }
    async confirmTrackEstablished(roomName, isLocalVideoReady) {
        const accepted = this.acceptedHandoff;
        if (!accepted
            || accepted.roomName !== roomName
            || !accepted.conferenceJoined
            || this.completionInFlight) {
            return;
        }
        if (!isLocalVideoReady()) {
            if (this.completionAttempt > 0) {
                this.failCompletion(accepted, 'Local video is no longer ready.');
            }
            return;
        }
        this.completionInFlight = true;
        this.clearTargetMediaTimeout();
        try {
            const confirmation = await this.callServer('POST', '/api/handoff/complete', {
                completionToken: accepted.completionToken,
                newDeviceId: this.deviceId,
                roomName
            });
            if (this.acceptedHandoff !== accepted) {
                return;
            }
            if (!confirmation || typeof confirmation !== 'object'
                || !('completed' in confirmation) || confirmation.completed !== true) {
                throw new Error('Handoff completion was not confirmed by the server.');
            }
            this.clearTargetMediaTimeout();
            this.acceptedHandoff = null;
            this.receivedHandoffToken = null;
            this.completionAttempt = 0;
            this.completionInFlight = false;
            this.emit('handoff_completed', { roomName });
            this.emit('handoff_progress', {
                stage: 'completed',
                message: 'Call moved successfully'
            });
        }
        catch (error) {
            if (this.acceptedHandoff !== accepted) {
                return;
            }
            this.completionInFlight = false;
            this.completionAttempt += 1;
            const retryDelay = COMPLETION_RETRY_DELAYS_MS[this.completionAttempt];
            if (typeof retryDelay === 'number'
                && this.acceptedHandoff
                && Date.now() + retryDelay + HANDOFF_HTTP_TIMEOUT_MS < accepted.completionExpiresAt) {
                setTimeout(() => {
                    if (this.acceptedHandoff === accepted) {
                        void this.confirmTrackEstablished(roomName, isLocalVideoReady);
                    }
                }, retryDelay);
                return;
            }
            this.failCompletion(accepted, getErrorMessage(error, 'Unable to confirm call move'));
        }
    }
    failCompletion(accepted, error) {
        if (this.acceptedHandoff !== accepted) {
            return;
        }
        this.clearTargetMediaTimeout();
        this.completionInFlight = false;
        this.acceptedHandoff = null;
        const progress = {
            stage: 'failed',
            message: 'Move confirmation could not be verified. Keep both devices connected until one shows the completed move.',
            error
        };
        this.emit('handoff_failed', progress);
        this.emit('handoff_progress', progress);
    }
    isCurrentSourceHandoff(token) {
        return Boolean(token && this.handoffInProgress && token === this.currentHandoffToken);
    }
    noteSourceHandoffConsumed(token) {
        if (!this.isCurrentSourceHandoff(token)) {
            return false;
        }
        // Once the target has consumed the token, give it enough time to join,
        // establish local video, and retry the server completion proof.
        this.startSourceHandoffTimeout(SOURCE_COMPLETION_TIMEOUT_MS);
        return true;
    }
    handleSourceRelease(roomName, token) {
        if (!this.isCurrentSourceHandoff(token)) {
            return false;
        }
        this.clearSourceHandoffTimeout();
        this.handoffInProgress = false;
        this.currentHandoffToken = null;
        this.emit('handoff_completed', { roomName });
        this.emit('handoff_progress', {
            stage: 'completed',
            message: 'Call moved successfully'
        });
        return true;
    }
    handleCancellation(token) {
        const role = token === this.currentHandoffToken
            ? 'source'
            : token === this.acceptedHandoff?.handoffToken
                ? 'target'
                : token === this.receivedHandoffToken
                    ? 'receiver'
                    : null;
        if (!role) {
            return null;
        }
        this.clearSourceHandoffTimeout();
        this.clearTargetMediaTimeout();
        this.handoffInProgress = false;
        this.currentHandoffToken = null;
        this.receivedHandoffToken = null;
        this.acceptedHandoff = null;
        this.completionInFlight = false;
        this.completionAttempt = 0;
        this.emit('handoff_declined', { token });
        this.emit('handoff_progress', {
            stage: 'failed',
            message: 'Call move was cancelled.',
            error: 'Call move was cancelled.'
        });
        if (role === 'target') {
            this.emit('handoff_target_abort', {
                stage: 'failed',
                message: 'The move was cancelled before this device became active.',
                error: 'Call move was cancelled.'
            });
        }
        return role;
    }
    // ============================================
    // QR CODE HELPERS
    // ============================================
    /**
     * Generate a URL that can be encoded as a QR code for the receiving device.
     */
    getHandoffQRData() {
        if (!this.currentHandoffToken) {
            return null;
        }
        return `${this.config.serverUrl}/handoff/${this.currentHandoffToken}`;
    }
    /**
     * Parse a handoff token from a QR code URL.
     */
    static parseHandoffUrl(url) {
        try {
            const parsed = new URL(url);
            const match = parsed.pathname.match(/^\/handoff\/([a-f0-9]+)$/);
            return match ? match[1] : null;
        }
        catch {
            return null;
        }
    }
    // ============================================
    // SERVER COMMUNICATION
    // ============================================
    async callServer(method, path, body) {
        const url = `${this.config.serverUrl}${path}`;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), HANDOFF_HTTP_TIMEOUT_MS);
        try {
            if (method === 'POST') {
                return unwrapApiResponse(await apiClient.post(url, body, { signal: controller.signal }));
            }
            throw new Error(`Unsupported handoff API method: ${method}`);
        }
        finally {
            clearTimeout(timeout);
        }
    }
    // ============================================
    // GETTERS
    // ============================================
    isScanning() {
        return this.scanning;
    }
    isHandoffInProgress() {
        return this.handoffInProgress;
    }
    getCompanionDevices() {
        return Array.from(this.companionDevices.values());
    }
    getDeviceId() {
        return this.deviceId;
    }
    getCurrentToken() {
        return this.currentHandoffToken;
    }
    isBleSupported() {
        return isReactNative() ? Boolean(this.rnBleManager) : isWebBluetoothSupported();
    }
    // ============================================
    // CLEANUP
    // ============================================
    disconnect() {
        this.stopScanning();
        this.clearSourceHandoffTimeout();
        this.clearTargetMediaTimeout();
        this.handoffInProgress = false;
        this.currentHandoffToken = null;
        this.receivedHandoffToken = null;
        this.acceptedHandoff = null;
        this.companionDevices.clear();
        this.bleDevice = null;
        if (this.rnBleManager) {
            this.rnBleManager.destroy();
            this.rnBleManager = null;
        }
        this.listeners.clear();
    }
}
// ============================================
// SINGLETON
// ============================================
const isBrowser = typeof window !== 'undefined';
let _instance = null;
function getInstance() {
    if (!isBrowser) {
        return null;
    }
    if (!_instance) {
        _instance = new DeviceHandoffService();
    }
    return _instance;
}
export const handoffService = {
    get instance() {
        return getInstance();
    },
    on: (...args) => getInstance()?.on(...args),
    off: (...args) => getInstance()?.off(...args),
    startScanning: (...args) => getInstance()?.startScanning(...args),
    stopScanning: () => getInstance()?.stopScanning(),
    requestCompanionDevice: (...args) => getInstance()?.requestCompanionDevice(...args),
    initiateHandoff: (...args) => getInstance()?.initiateHandoff(...args),
    receiveHandoff: (...args) => getInstance()?.receiveHandoff(...args),
    acceptHandoff: (...args) => getInstance()?.acceptHandoff(...args),
    declineHandoff: (...args) => getInstance()?.declineHandoff(...args),
    confirmTrackEstablished: (...args) => getInstance()?.confirmTrackEstablished(...args),
    noteSourceHandoffConsumed: (...args) => getInstance()?.noteSourceHandoffConsumed(...args),
    noteConferenceJoined: (...args) => getInstance()?.noteConferenceJoined(...args),
    handleSourceRelease: (...args) => getInstance()?.handleSourceRelease(...args),
    isCurrentSourceHandoff: (...args) => Boolean(getInstance()?.isCurrentSourceHandoff(...args)),
    handleCancellation: (...args) => getInstance()?.handleCancellation(...args),
    getHandoffQRData: () => getInstance()?.getHandoffQRData(),
    isScanning: () => Boolean(getInstance()?.isScanning()),
    isHandoffInProgress: () => Boolean(getInstance()?.isHandoffInProgress()),
    getCompanionDevices: () => getInstance()?.getCompanionDevices() || [],
    getDeviceId: () => getInstance()?.getDeviceId() || '',
    isBleSupported: () => Boolean(getInstance()?.isBleSupported()),
    disconnect: () => getInstance()?.disconnect(),
    parseHandoffUrl: DeviceHandoffService.parseHandoffUrl
};
export default DeviceHandoffService;
